import {
  CaretDownOutlined,
  DeleteOutlined,
  GlobalOutlined,
  MailOutlined,
  MobileOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { AngleDownLightIcon } from '@mm/icons';
import SuppressionSettingsController from '@mm/state/controllers/SuppressionSettingsController';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { uncompliantDomainsListSelector } from '@mm/state/selectors/envSelectors';
import { ReactComponent as ManualSuppressionIcon } from 'assets/customize.svg';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  errorToast,
  successToast,
} from '../../../../../../../../common/helpers/toast';
import { isUrl } from '../../../../../../../components/SlideoutMenu/menus/SlideoutEmail/editor/utils';
import { colors } from 'packages/utils';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import css from './ManualIntegrations.module.css';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  CaptureActionEnum,
  SuppressionSettingsModel,
} from '@mm/state/model/SuppressionSettingsModel';
import { ContactSuppressionHeader } from '../ContactSuppressions/ContactSuppressionHeader';
import {
  Collapse,
  CollapseProps,
  Input,
  Transfer,
  Tree,
  type GetProp,
  type TransferProps,
  type TreeDataNode,
} from 'antd';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';
import { getEnv } from '@mm/state/actions/networkActions';

// Define types for TreeTransfer component
type TransferItem = GetProp<TransferProps, 'dataSource'>[number];

interface TreeTransferProps {
  dataSource: TreeDataNode[];
  targetKeys: string[];
  onChange: (keys: string[]) => void;
}

// Expand Icon component for Collapse
const ExpandIcon: React.FC<{ isActive?: boolean }> = ({ isActive }) => (
  <CaretDownOutlined style={{ fontSize: 20 }} rotate={isActive ? 180 : 0} />
);

// Function to render expand icon that can be referenced directly
const renderExpandIconFunction = ({ isActive }: { isActive?: boolean }) => (
  <ExpandIcon isActive={isActive} />
);

// EU and Canadian Emails Header component
const EUCanadianEmailsHeader = () => (
  <div
    className={classNames(
      css.headerContainer,
      'integrations__header-container',
    )}
  >
    <div className={css.headerTitle}>
      <div className={css.title}>EU and Canadian Emails</div>
      <div className={css.subheading}>
        Manage domains that require special handling under GDPR and Canadian
        regulations
      </div>
    </div>
    <div className={css.header}>
      <div className={classNames(css.listIcon, 'integrations__list-icon')}>
        <GlobalOutlined />
      </div>
    </div>
  </div>
);

// Customize Tree Transfer
const TreeTransfer: React.FC<TreeTransferProps> = ({
  dataSource,
  targetKeys = [],
  onChange,
}) => {
  // State to manage group selections completely separate from Transfer's selection mechanism
  const [groupSelections, setGroupSelections] = useState<{
    left: Set<string>;
    right: Set<string>;
  }>({
    left: new Set(),
    right: new Set(),
  });

  // State to track Transfer's selected keys (items ready for transfer)
  const [transferSelectedKeys, setTransferSelectedKeys] = useState<string[]>(
    [],
  );

  // Clear selections when targetKeys change (after transfer)
  useEffect(() => {
    setGroupSelections({
      left: new Set(),
      right: new Set(),
    });
    setTransferSelectedKeys([]);
  }, [targetKeys]);

  // Helper function to get all child keys for a parent group
  const getChildKeys = useCallback(
    (parentKey: string): string[] => {
      const group = dataSource.find(group => group.key === parentKey);
      return group?.children?.map(child => child.key as string) || [];
    },
    [dataSource],
  );

  // Update Transfer selected keys whenever group selections change
  useEffect(() => {
    const newSelectedKeys = new Set<string>();

    // Add all items selected via group selection on the left side
    for (const groupKey of groupSelections.left) {
      const childKeys = getChildKeys(groupKey);
      childKeys.forEach(childKey => {
        // Only include if it's available on the left side (not in targetKeys)
        if (!targetKeys.includes(childKey)) {
          newSelectedKeys.add(childKey);
        }
      });
    }

    // Add all items selected via group selection on the right side
    for (const groupKey of groupSelections.right) {
      const childKeys = getChildKeys(groupKey);
      childKeys.forEach(childKey => {
        // Only include if it's available on the right side (in targetKeys)
        if (targetKeys.includes(childKey)) {
          newSelectedKeys.add(childKey);
        }
      });
    }

    // Update transferSelectedKeys to include group selections
    setTransferSelectedKeys(Array.from(newSelectedKeys));
  }, [groupSelections, targetKeys, getChildKeys]);

  const transferDataSource: TransferItem[] = [];
  function flatten(list: TreeDataNode[] = []) {
    list.forEach(item => {
      // Only add leaf nodes (actual domains) to transferDataSource
      if (!item.children || item.children.length === 0) {
        transferDataSource.push(item as TransferItem);
      } else {
        flatten(item.children);
      }
    });
  }
  flatten(dataSource);

  // Override onChange to include group-selected items
  const handleTransferChange = (newTargetKeys: string[]) => {
    // The Transfer component already includes our selected items, so we can use newTargetKeys directly
    onChange(newTargetKeys);
  };

  return (
    <Transfer
      targetKeys={targetKeys}
      dataSource={transferDataSource}
      className="tree-transfer"
      render={item => item.title || ''}
      showSelectAll={false}
      selectedKeys={transferSelectedKeys}
      onSelectChange={(sourceSelectedKeys, targetSelectedKeys) => {
        // Update our managed state with both source and target selections
        setTransferSelectedKeys([
          ...sourceSelectedKeys.map(k => k as string),
          ...targetSelectedKeys.map(k => k as string),
        ]);
      }}
      onChange={handleTransferChange}
    >
      {({
        direction,
        onItemSelect: _onItemSelect,
        selectedKeys: _selectedKeys,
      }) => {
        // Helper function to determine if all children are selected
        const areAllChildrenSelected = (
          parentKey: string,
          currentKeys: React.Key[],
          availableChildren: string[],
        ): boolean => {
          const childKeys = getChildKeys(parentKey);
          if (childKeys.length === 0) return false;

          // Only check children that are available in the current side
          const relevantChildren = childKeys.filter(childKey =>
            availableChildren.includes(childKey),
          );
          if (relevantChildren.length === 0) return false;

          return relevantChildren.every(childKey =>
            currentKeys.includes(childKey),
          );
        };

        // Helper function to handle parent group selection - no more onItemSelect calls!
        const handleGroupSelection = (
          groupKey: string,
          shouldSelect: boolean,
        ) => {
          setGroupSelections(prev => {
            const newSelections = { ...prev };
            const currentSideSelections =
              direction === 'left' ? prev.left : prev.right;
            const newSideSelections = new Set(currentSideSelections);

            if (shouldSelect) {
              newSideSelections.add(groupKey);
            } else {
              newSideSelections.delete(groupKey);
            }

            if (direction === 'left') {
              newSelections.left = newSideSelections;
            } else {
              newSelections.right = newSideSelections;
            }

            return newSelections;
          });
        };

        // Get the displayed checked keys including both Transfer selections and group selections
        const getDisplayedCheckedKeys = (
          availableChildren: string[],
          treeData: TreeDataNode[],
        ): React.Key[] => {
          const currentSideGroupSelections =
            direction === 'left' ? groupSelections.left : groupSelections.right;
          const result = new Set<React.Key>(transferSelectedKeys);

          // Add all group selections for this side
          currentSideGroupSelections.forEach(groupKey => {
            result.add(groupKey);
            // Add all children of selected groups
            const childKeys = getChildKeys(groupKey);
            childKeys.forEach(childKey => {
              if (availableChildren.includes(childKey)) {
                result.add(childKey);
              }
            });
          });

          // Add parent groups that have all available children selected (either via Transfer or group selection)
          treeData.forEach(group => {
            const groupKey = group.key as string;
            if (
              !result.has(groupKey) &&
              areAllChildrenSelected(
                groupKey,
                Array.from(result),
                availableChildren,
              )
            ) {
              result.add(groupKey);
            }
          });

          return Array.from(result);
        };

        if (direction === 'left') {
          // Left side: show only available (non-selected) items in tree structure
          const leftTreeData = dataSource
            .map(group => ({
              ...group,
              children: group.children?.filter(
                child => !targetKeys.includes(child.key as string),
              ),
            }))
            .filter(group => group.children && group.children.length > 0);

          const availableLeftChildren = leftTreeData.flatMap(
            group => group.children?.map(child => child.key as string) || [],
          );

          return (
            <Tree
              blockNode
              checkable
              defaultExpandAll
              checkedKeys={getDisplayedCheckedKeys(
                availableLeftChildren,
                leftTreeData,
              )}
              treeData={leftTreeData}
              onCheck={(_, { node: { key } }) => {
                // Check if this is a parent group or child item
                const isParentGroup = leftTreeData.some(
                  group => group.key === key,
                );

                if (isParentGroup) {
                  // For parent groups, use our new group selection handling
                  const currentSideGroupSelections = groupSelections.left;
                  const shouldSelect = !currentSideGroupSelections.has(
                    key as string,
                  );
                  handleGroupSelection(key as string, shouldSelect);
                } else {
                  // Handle individual child selection by updating our managed selectedKeys
                  const isSelected = transferSelectedKeys.includes(
                    key as string,
                  );
                  if (isSelected) {
                    // Remove from selection
                    setTransferSelectedKeys(prev =>
                      prev.filter(k => k !== key),
                    );
                  } else {
                    // Add to selection
                    setTransferSelectedKeys(prev => [...prev, key as string]);
                  }
                }
              }}
              onSelect={(_, { node: { key } }) => {
                const isParentGroup = leftTreeData.some(
                  group => group.key === key,
                );

                if (isParentGroup) {
                  const currentSideGroupSelections = groupSelections.left;
                  const shouldSelect = !currentSideGroupSelections.has(
                    key as string,
                  );
                  handleGroupSelection(key as string, shouldSelect);
                } else {
                  // Handle individual child selection by updating our managed selectedKeys
                  const isSelected = transferSelectedKeys.includes(
                    key as string,
                  );
                  if (isSelected) {
                    // Remove from selection
                    setTransferSelectedKeys(prev =>
                      prev.filter(k => k !== key),
                    );
                  } else {
                    // Add to selection
                    setTransferSelectedKeys(prev => [...prev, key as string]);
                  }
                }
              }}
            />
          );
        }

        // Right side: show only selected items in tree structure
        const rightTreeData = dataSource
          .map(group => ({
            ...group,
            children: group.children?.filter(child =>
              targetKeys.includes(child.key as string),
            ),
          }))
          .filter(group => group.children && group.children.length > 0);

        const availableRightChildren = rightTreeData.flatMap(
          group => group.children?.map(child => child.key as string) || [],
        );

        return (
          <Tree
            blockNode
            checkable
            defaultExpandAll
            checkedKeys={getDisplayedCheckedKeys(
              availableRightChildren,
              rightTreeData,
            )}
            treeData={rightTreeData}
            onCheck={(_, { node: { key } }) => {
              // Check if this is a parent group or child item
              const isParentGroup = rightTreeData.some(
                group => group.key === key,
              );

              if (isParentGroup) {
                // For parent groups on right side, determine selection state
                const currentSideGroupSelections = groupSelections.right;
                const shouldSelect = !currentSideGroupSelections.has(
                  key as string,
                );
                handleGroupSelection(key as string, shouldSelect);
              } else {
                // Handle individual child selection by updating our managed selectedKeys
                const isSelected = transferSelectedKeys.includes(key as string);
                if (isSelected) {
                  // Remove from selection
                  setTransferSelectedKeys(prev => prev.filter(k => k !== key));
                } else {
                  // Add to selection
                  setTransferSelectedKeys(prev => [...prev, key as string]);
                }
              }
            }}
            onSelect={(_, { node: { key } }) => {
              const isParentGroup = rightTreeData.some(
                group => group.key === key,
              );

              if (isParentGroup) {
                // For parent groups on right side, determine selection state
                const currentSideGroupSelections = groupSelections.right;
                const shouldSelect = !currentSideGroupSelections.has(
                  key as string,
                );
                handleGroupSelection(key as string, shouldSelect);
              } else {
                // Handle individual child selection by updating our managed selectedKeys
                const isSelected = transferSelectedKeys.includes(key as string);
                if (isSelected) {
                  // Remove from selection
                  setTransferSelectedKeys(prev => prev.filter(k => k !== key));
                } else {
                  // Add to selection
                  setTransferSelectedKeys(prev => [...prev, key as string]);
                }
              }
            }}
          />
        );
      }}
    </Transfer>
  );
};

export const ManualIntegrations = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const uncompliantDomainsList = useSelector(uncompliantDomainsListSelector);
  const dispatch = useDispatch();

  const [settings, setSettings] = useState<SuppressionSettingsModel[]>([]);
  const [inputs, setInputs] = useState<{ [key in CaptureActionEnum]: string }>({
    [CaptureActionEnum.do_not_send]: '',
    [CaptureActionEnum.do_not_capture]: '',
  });
  const [viewFull, setViewFull] = useState<{
    [key in CaptureActionEnum]: boolean;
  }>({
    [CaptureActionEnum.do_not_send]: false,
    [CaptureActionEnum.do_not_capture]: false,
  });
  const [busyCustom, setBusyCustom] = useState(false);
  const [customListSource, setCustomListSource] = useState<{
    [key in CaptureActionEnum]: any[];
  }>({
    [CaptureActionEnum.do_not_send]: [],
    [CaptureActionEnum.do_not_capture]: [],
  });

  // EU and Canadian Emails state
  const [targetKeys, setTargetKeys] = useState<string[]>([]); // Currently selected domains (right side)
  const [localTargetKeys, setLocalTargetKeys] = useState<string[]>([]); // Local state for transfer UI
  const [euDomainTreeData, setEuDomainTreeData] = useState<TreeDataNode[]>([]);

  const supressionSetting = settings.find(
    s => s.captureAction === CaptureActionEnum.do_not_send,
  );
  const unsubscribeSettings = settings.find(
    s => s.captureAction === CaptureActionEnum.do_not_capture,
  );

  const { openNewConfirmModal, closeModal } = useModalActions();
  const CAP = 3;

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_CONTACT_SUPPRESSION,
    featureFlag: 'contactSuppression',
  };

  // Setup domains tree data structure
  const setupDomainsTree = (
    allDomains: string[],
    selectedDomains: string[] = [],
  ) => {
    // Define comprehensive domain groupings
    const domainGroups = {
      eu: {
        title: 'European Union Domains',
        domains: [
          'at',
          'be',
          'bg',
          'hr',
          'cy',
          'cz',
          'dk',
          'ee',
          'fi',
          'fr',
          'de',
          'gr',
          'hu',
          'ie',
          'it',
          'lv',
          'lt',
          'lu',
          'mt',
          'nl',
          'pl',
          'pt',
          'ro',
          'sk',
          'si',
          'es',
          'se',
          'eu',
        ],
      },
      uk: {
        title: 'United Kingdom',
        domains: ['uk'],
      },
      canada: {
        title: 'Canada',
        domains: ['ca'],
      },
      swiss: {
        title: 'Switzerland',
        domains: ['ch'],
      },
    };

    // Create tree data structure with all available domains
    const treeData: TreeDataNode[] = [];

    Object.entries(domainGroups).forEach(([groupKey, groupInfo]) => {
      // Include all domains from this group that exist in allDomains
      const groupDomains = groupInfo.domains.filter(domain =>
        allDomains.includes(domain),
      );

      if (groupDomains.length > 0) {
        treeData.push({
          title: groupInfo.title,
          key: `${groupKey}-group`,
          children: groupDomains.map(domain => ({
            title: domain.toLowerCase(),
            key: domain,
          })),
        });
      }
    });

    setEuDomainTreeData(treeData);

    // Set initial target keys
    if (selectedDomains.length > 0) {
      setTargetKeys(selectedDomains);
      setLocalTargetKeys(selectedDomains);
    }
  };

  // Handle change in tree transfer selection (only for UI, doesn't save immediately)
  const handleTreeTransferChange = (newTargetKeys: string[]) => {
    setLocalTargetKeys(newTargetKeys);
  };

  // Actually save the uncompliant domains to backend
  const saveUncompliantDomains = async (domainsToSave: string[]) => {
    try {
      // Get or create the suppression setting for do_not_send action
      const suppressionSetting =
        supressionSetting ||
        (await SuppressionSettingsController.createSettings(facebookPageId, {
          captureAction: CaptureActionEnum.do_not_send,
        }));

      // Update the suppression setting with uncompliant domains data
      // Note: Frontend uses camelCase which gets automatically converted to snake_case by the controller
      await SuppressionSettingsController.updateSettings(
        facebookPageId,
        suppressionSetting.id,
        {
          ...suppressionSetting,
          suppressUncompliantDomains: domainsToSave.length > 0,
          uncompliantDomainsList: domainsToSave,
        },
      );

      setTargetKeys(domainsToSave);
      setLocalTargetKeys(domainsToSave);
      successToast('Domain settings updated successfully');
    } catch (err) {
      errorToast('Error updating domain settings', 'top-right');
    }
  };

  const customListView = (captureAction: CaptureActionEnum) =>
    viewFull[captureAction]
      ? customListSource[captureAction]
      : customListSource[captureAction].slice(0, CAP);

  const onFullListClick = (captureAction: CaptureActionEnum) => {
    setViewFull(prev => ({
      ...prev,
      [captureAction]: true,
    }));
  };

  const fetchSettings = async () => {
    const res = await SuppressionSettingsController.getSettings(facebookPageId);
    setSettings(res);

    // Also fetch environment data to get all available domains
    const envData = await dispatch(getEnv() as any);

    const requiredCaptureActions = [
      CaptureActionEnum.do_not_send,
      CaptureActionEnum.do_not_capture,
    ];

    const sendSignalSetting = res.find(s => s.data?.sendSignal);

    const updatedSettings = await Promise.all(
      requiredCaptureActions.map(async action => {
        let setting = res.find(s => s.captureAction === action);
        if (!setting?.id) {
          setting = await SuppressionSettingsController.createSettings(
            facebookPageId,
            { captureAction: action },
          );

          if (sendSignalSetting?.data) {
            setting = await SuppressionSettingsController.updateSettings(
              facebookPageId,
              setting.id,
              { data: sendSignalSetting.data },
            );
          }
        }
        return setting;
      }),
    );

    const listSources: { [key in CaptureActionEnum]: any[] } = {
      [CaptureActionEnum.do_not_send]: [],
      [CaptureActionEnum.do_not_capture]: [],
    };

    updatedSettings.forEach(setting => {
      listSources[setting.captureAction] = setting.list || [];
    });

    setCustomListSource(listSources);

    // Get the full list of available domains from env and currently selected domains from suppression settings
    const allDomainsFromEnv = envData?.uncompliantDomainsList ||
      uncompliantDomainsList || [
        'ca',
        'at',
        'be',
        'bg',
        'hr',
        'cy',
        'cz',
        'dk',
        'ee',
        'fi',
        'fr',
        'de',
        'gr',
        'hu',
        'ie',
        'it',
        'lv',
        'lt',
        'lu',
        'mt',
        'nl',
        'pl',
        'pt',
        'ro',
        'sk',
        'si',
        'es',
        'se',
        'eu',
        'uk',
        'ch',
      ];

    // Extract currently selected uncompliant domains from suppression setting
    const suppressionSetting = updatedSettings.find(
      s => s.captureAction === CaptureActionEnum.do_not_send,
    );
    const currentlySelectedDomains =
      suppressionSetting?.uncompliantDomainsList || [];

    // Setup tree with all available domains and current selection
    setupDomainsTree(allDomainsFromEnv, currentlySelectedDomains);
  };

  const handleDelete = async (
    id: string | number,
    captureAction: CaptureActionEnum,
  ) => {
    setBusyCustom(true);
    const setting =
      captureAction === CaptureActionEnum.do_not_send
        ? supressionSetting
        : unsubscribeSettings;

    try {
      if (setting?.id) {
        const list = customListSource[captureAction].filter(
          item => item !== id,
        );

        await SuppressionSettingsController.updateSettings(
          facebookPageId,
          setting.id,
          {
            ...setting,
            listToDelete: [...(setting.listToDelete || []), id],
          },
        );

        setCustomListSource(prev => ({
          ...prev,
          [captureAction]: list,
        }));

        successToast(`Removed: "${id}"`);
      }
    } catch (err) {
      errorToast(`Error removing: "${id}"`, 'top-right');
    } finally {
      setBusyCustom(false);
    }
  };

  const handleCustomDeleteClick = (
    value: string | number,
    captureAction: CaptureActionEnum,
  ) => {
    openNewConfirmModal({
      title: `Manually add Emails/Domains`,
      subtitle: `Are you sure you want to remove "${value}"?`,
      onResolve: async () => {
        await handleDelete(value, captureAction);
        closeModal();
      },
      onReject: closeModal,
    });
  };

  const handleAddCustomSuppressionClick = async (
    value: string,
    captureAction: CaptureActionEnum,
  ) => {
    setBusyCustom(true);
    const setting =
      captureAction === CaptureActionEnum.do_not_send
        ? supressionSetting
        : unsubscribeSettings;

    try {
      if (setting?.id) {
        const list = Array.from(
          new Set([value, ...customListSource[captureAction]]),
        );

        await SuppressionSettingsController.updateSettings(
          facebookPageId,
          setting.id,
          {
            ...setting,
            list,
            captureAction,
          },
        );

        setCustomListSource(prev => ({
          ...prev,
          [captureAction]: list,
        }));

        setInputs(prev => ({
          ...prev,
          [captureAction]: '',
        }));

        successToast(`Added: "${value}"`);
      }
    } catch (err) {
      errorToast(`Error adding: "${value}"`, 'top-right');
    } finally {
      setBusyCustom(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFetchSettings = useCallback(fetchSettings, [facebookPageId]);

  useEffect(() => {
    memoizedFetchSettings();
    // setupEnvDomains will be called as fallback from fetchSettings if needed
  }, [memoizedFetchSettings]);

  const renderContent = (captureAction: CaptureActionEnum) => (
    <div className={css.content}>
      <div className={css.inputField}>
        <Input
          type="text"
          placeholder={`Enter Domains, Emails, Phone Numbers to ${
            captureAction === CaptureActionEnum.do_not_send
              ? 'suppress'
              : 'unsubscribe'
          }`}
          value={inputs[captureAction]}
          onChange={e =>
            setInputs(prev => ({
              ...prev,
              [captureAction]: e?.target?.value.trim(),
            }))
          }
          className={css.input}
          variant="outlined"
          onPressEnter={() =>
            handleAddCustomSuppressionClick(
              inputs[captureAction],
              captureAction,
            )
          }
        />
        <Button
          onClick={() =>
            handleAddCustomSuppressionClick(
              inputs[captureAction],
              captureAction,
            )
          }
          disabled={!inputs[captureAction] || busyCustom}
          css={{ container: css.primaryButton }}
        >
          Add
        </Button>
      </div>

      <div className={css.inputList}>
        {customListView(captureAction)?.map(item => {
          const icon =
            typeof item === 'string' && item.includes('@') ? (
              <MailOutlined />
            ) : typeof item === 'string' && item.startsWith('+') ? (
              <MobileOutlined />
            ) : typeof item === 'string' && isUrl(item) ? (
              <GlobalOutlined />
            ) : null;

          return (
            <div className={css.listItem} key={item}>
              <span className={css.inputListTitle}>
                {icon}
                {item}
              </span>
              <span className={css.inputListActions}>
                <div className={css.inputListAction}>
                  <div
                    className={css.listAction}
                    onClick={
                      busyCustom
                        ? undefined
                        : () => handleCustomDeleteClick(item, captureAction)
                    }
                  >
                    <DeleteOutlined />
                  </div>
                </div>
              </span>
            </div>
          );
        })}

        {customListSource[captureAction].length > CAP &&
          !viewFull[captureAction] && (
            <div className={css.inputListItem}>
              <span
                className={classNames(css.inputListTitle, css.clickable)}
                onClick={() => onFullListClick(captureAction)}
              >
                View full list <AngleDownLightIcon color={colors.gray9} />
              </span>
            </div>
          )}
      </div>
    </div>
  );

  const renderEUContent = () => {
    const hasChanges =
      JSON.stringify(localTargetKeys.sort()) !==
      JSON.stringify(targetKeys.sort());

    return (
      <div className={css.content}>
        <div className={css.domainListWrapper}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
            }}
          >
            <strong style={{ fontSize: '14px', color: '#333' }}>
              Allowed EU and Canadian Domains
            </strong>
            <strong style={{ fontSize: '14px', color: '#333' }}>
              Suppressed EU and Canadian Domains
            </strong>
          </div>
          <TreeTransfer
            dataSource={euDomainTreeData}
            targetKeys={localTargetKeys}
            onChange={handleTreeTransferChange}
          />
        </div>

        {hasChanges && (
          <div className={css.actionButtons}>
            <Button
              onClick={() => setLocalTargetKeys(targetKeys)}
              css={{ container: css.cancelButton }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveUncompliantDomains(localTargetKeys)}
              css={{ container: css.saveButton }}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
    );
  };

  const items: CollapseProps['items'] = [
    {
      key: CaptureActionEnum.do_not_send,
      label: <ContactSuppressionHeader isSuppression />,
      children: renderContent(CaptureActionEnum.do_not_send),
    },
    {
      key: CaptureActionEnum.do_not_capture,
      label: <ContactSuppressionHeader isSuppression={false} />,
      children: renderContent(CaptureActionEnum.do_not_capture),
    },
    {
      key: 'eu_canadian_emails',
      label: <EUCanadianEmailsHeader />,
      children: renderEUContent(),
    },
  ];

  return (
    <div className={css.container}>
      <BaseIntegration
        openOnMount
        titleComponent={
          <BaseIntegrationHeader
            title="Manually add Emails/Domains"
            subtitle="Choose emails, domains, and phone numbers that your automations will not engage with.
            We automatically suppress bounces, unsubscribes and other emails we believe to be problematic."
            icon={<ManualSuppressionIcon />}
            headerRightComponent={
              <HelpCenter
                title=""
                helpLink="https://customers.ai/help/l/en/article/uhl81gxos2-enable-email-suppression-list"
                icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
              />
            }
            css={{ icon: 'integrations__header-icon-manual-suppression' }}
            {...sectionProps}
          />
        }
      >
        <div className={css.contentWrapper}>
          <div className={css.label}>Sources</div>
          <Collapse
            items={items}
            defaultActiveKey={[
              CaptureActionEnum.do_not_send,
              CaptureActionEnum.do_not_capture,
              'eu_canadian_emails',
            ]}
            className={classNames('integrations__collapse', css.collapse)}
            expandIcon={renderExpandIconFunction}
          />
        </div>
      </BaseIntegration>
    </div>
  );
};
